import { Box, BoxProps } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemProps } from '@mui/system/Box';
import { forwardRef } from 'react';

/**
 * MUI v9 dropped system props (`gap`, `flexGrow`, `justifyContent`, …) from Box in favour of `sx`.
 * Flex keeps accepting them and folds them into `sx`, so its many call sites stay unchanged.
 */
type FlexProps = { center?: boolean; col?: boolean } & BoxProps & SystemProps<Theme>;

const SYSTEM_PROP_KEYS = new Set([
  'alignContent',
  'alignItems',
  'alignSelf',
  'border',
  'borderBottom',
  'borderColor',
  'borderLeft',
  'borderRadius',
  'borderRight',
  'borderTop',
  'bottom',
  'boxShadow',
  'color',
  'columnGap',
  'display',
  'flex',
  'flexBasis',
  'flexDirection',
  'flexGrow',
  'flexShrink',
  'flexWrap',
  'fontFamily',
  'fontSize',
  'fontStyle',
  'fontWeight',
  'gap',
  'gridArea',
  'gridAutoColumns',
  'gridAutoFlow',
  'gridAutoRows',
  'gridColumn',
  'gridRow',
  'gridTemplateAreas',
  'gridTemplateColumns',
  'gridTemplateRows',
  'height',
  'justifyContent',
  'justifyItems',
  'justifySelf',
  'left',
  'letterSpacing',
  'lineHeight',
  'm',
  'margin',
  'marginBottom',
  'marginLeft',
  'marginRight',
  'marginTop',
  'marginX',
  'marginY',
  'maxHeight',
  'maxWidth',
  'mb',
  'minHeight',
  'minWidth',
  'ml',
  'mr',
  'mt',
  'mx',
  'my',
  'order',
  'overflow',
  'p',
  'padding',
  'paddingBottom',
  'paddingLeft',
  'paddingRight',
  'paddingTop',
  'paddingX',
  'paddingY',
  'pb',
  'pl',
  'position',
  'pr',
  'pt',
  'px',
  'py',
  'right',
  'rowGap',
  'textAlign',
  'textOverflow',
  'textTransform',
  'top',
  'typography',
  'visibility',
  'whiteSpace',
  'width',
  'zIndex',
  'bgcolor',
  'backgroundColor',
]);

const Flex = forwardRef<HTMLDivElement, FlexProps>((props, ref) => {
  const { center, col, children, sx, ...rest } = props;

  const systemSx: Record<string, unknown> = {};
  const boxProps: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(rest)) {
    if (SYSTEM_PROP_KEYS.has(key)) {
      systemSx[key] = value;
    } else {
      boxProps[key] = value;
    }
  }

  return (
    <Box
      ref={ref}
      {...boxProps}
      sx={[
        {
          display: 'flex',
          flexDirection: col ? 'column' : undefined,
          justifyContent: center ? 'center' : undefined,
          alignItems: center ? 'center' : undefined,
        },
        systemSx,
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      {children}
    </Box>
  );
});

export default Flex;
